import {CSSProperties} from 'react'

import Colors from '@utils/Colors.js'

import {hoverOverlay} from '../styles.js'

// All table components take their look from here: sizes, spacing and colours of the header, rows, cells, footer and
// the menus in the header, plus the shared styles built from them. Change a value here instead of in a component, so
// every part of the table stays aligned.

export const tableSizes = {
    headerHeight: 42,
    rowHeight: 50,
    // Left and right padding of header and body cells
    cellPaddingX: 12,
    // Space between the checkbox (36 wide with its hover circle) and the first column
    checkboxGap: 20,
    borderWidth: 1,
    headerRadius: '4px 4px 0px 0px',
    // The drag handle at the right edge of a header cell
    resizerWidth: 8
}

// Column widths and how the columns are fitted to the table width
export const tableColumns = {
    // Width of a column that sets none
    defaultWidth: 100,
    // No column gets narrower than this, by resizing or fitting (column.minWidth can raise it)
    minWidth: 50,
    // Columns narrower than this (icon columns) keep their width when the columns are fitted to the table
    fixedWidth: 75,
    defaultFlex: 1,
    // The columns are fitted to the table width minus the checkbox column (checkbox + checkboxGap) and the scrollbar
    fitOffsetCheckbox: 56,
    fitOffsetScrollbar: 15
}

export const tableSpacing = {
    // Around the message in the body when there are no rows
    emptyPadding: 20,
    // Above the spinner while loading
    loadingTop: 20,
    footerPadding: '5px 10px 5px 10px',
    // Between a header title and its sort icon
    sortIconGap: 3
}

// The menus in the header: settings, sort options of a column and the selection menu
export const tableMenus = {
    // The icon buttons are 36 high, centred in the header. The selection menu button sits over the space after the
    // checkbox, the settings button over the end of the last column.
    buttonTop: 3,
    selectionMenuLeft: -28,
    settingsWidth: 300,
    settingsPadding: 20,
    dropdownWidth: 300,
    selectionMenuWidth: 240,
    // Menus grow to the height of the table below the header before they scroll, but never get smaller than this
    minHeight: 240
}

export const tablePagination = {
    pageFontSize: 14,
    activePageFontSize: 16,
    pageSizeOptions: [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 25, 30, 40, 50],
    defaultPageSize: 40
}

export const tableColors = {
    background: Colors.backgroundWhite,
    rowHover: hoverOverlay.subtle,
    rowBorder: Colors.grey20,
    headerBorder: Colors.grey30,
    resizerBorder: Colors.grey30,
    footerBorder: Colors.grey20,
    headerText: Colors.textDark,
    emptyText: Colors.textMedium,
    activePage: Colors.brandColor
}

const border = (color: string) => `${tableSizes.borderWidth}px solid ${color}`

// The header row
export const headerRowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    height: tableSizes.headerHeight,
    width: '100%',
    background: tableColors.background,
    borderRadius: tableSizes.headerRadius,
    borderBottom: border(tableColors.headerBorder),
    userSelect: 'none'
}

// A body row; the hover background lies over the row's own background (rowBackground), see TableRow
export function getRowStyle({hovered}: {hovered: boolean}): CSSProperties {
    return {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        width: '100%',
        height: tableSizes.rowHeight,
        background: hovered ? tableColors.rowHover : 'transparent',
        borderBottom: border(tableColors.rowBorder)
    }
}

export function getRowBackground(background?: string) {
    return background || tableColors.background
}

// A header or body cell of a column
export function getCellStyle(width: number): CSSProperties {
    return {
        display: 'flex',
        alignItems: 'center',
        flex: `0 0 ${width}px`,
        paddingLeft: tableSizes.cellPaddingX,
        paddingRight: tableSizes.cellPaddingX,
        minWidth: 0 // important to prevent overflow
    }
}

// The cell with the checkbox (header and body) and the space after the checkbox
export const checkboxCellStyle: CSSProperties = {display: 'flex', alignItems: 'center', alignSelf: 'stretch', flexShrink: 0}
export const checkboxGapStyle: CSSProperties = {width: tableSizes.checkboxGap, height: '100%', flexShrink: 0}

export function getHeaderTitleStyle({sortable}: {sortable: boolean}): CSSProperties {
    return {
        cursor: sortable ? 'pointer' : undefined,
        color: tableColors.headerText,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    }
}

export const resizerStyle: CSSProperties = {
    position: 'absolute',
    right: 0,
    top: 0,
    height: '100%',
    width: tableSizes.resizerWidth,
    cursor: 'col-resize',
    userSelect: 'none',
    borderRight: border(tableColors.resizerBorder)
}

export const footerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    background: tableColors.background,
    borderTop: border(tableColors.footerBorder),
    padding: tableSpacing.footerPadding
}

// A header menu's content: scrolls once it reaches maxHeight (see tableMenus.minHeight)
export function getMenuStyle(width: number, maxHeight: number): CSSProperties {
    return {width, maxHeight, overflowY: 'auto'}
}

// The anchor of a header menu button (settings, selection), positioned over the header row
export const menuAnchorStyle: CSSProperties = {position: 'relative', alignSelf: 'stretch', width: 0, flexShrink: 0}
